import { Box, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IComposableDashboardContext, unPinDashboard, IComposableDashboard } from 'wisestep-api';
import { WsButton, WsDialog, WsTypoColors, WsTypoVariants, WsTypography } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { useComposableDashboardsContext } from '../../../features/analyticsPage/hooks/useComposableDashboardsContext';
import Unpin from '../../../images/Unpin';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { pinUnpinPromptText } from '../../analyticsPage/helper/getDefaultDashboardValues';

export interface IUnpinDashboardProps {
  selectId: string | null;
  handleUnpinDashboard?: (string) => void;
  fromHome?: boolean;
}

const UnpinDashboard = ({
  selectId,
  handleUnpinDashboard,
  fromHome = false,
}: IUnpinDashboardProps) => {
  const { selectedDashboard } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const [openUnpinDialog, setOpenUnpinDialog] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const isDashboardCreator = (dashboard: IComposableDashboard) =>
    dashboard.createdBy === workspace.userId;
  const workspace = useWorkspaceContext();

  const onConfirm = async (dashboardId: string) => {
    setOpenUnpinDialog(false);
    if (!fromHome) {
      updateUnpinDashboard(dashboardId);
    } else {
      handleUnpinDashboard && handleUnpinDashboard('');
    }
  };
  const updateUnpinDashboard = async (dashboardId: string) => {
    try {
      if (selectedDashboard?.id) {
        await unPinDashboard(dashboardId);
        setNotification(
          success(
            `Dashboard ${selectedDashboard?.name ?? ''} has been un-pinned from the Home page`
          )
        );
        handleUnpinDashboard && handleUnpinDashboard(selectedDashboard?.id);
      }
    } catch (error) {
      setNotification(failed('Failed to unpin the dashboard. Please try again!'));
    }
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      {selectedDashboard?.id && isDashboardCreator(selectedDashboard) ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            borderRadius: '0.45',
          }}
        >
          <WsTypography
            hoverText={'Unpin from Home'}
            bold={true}
            display="inline"
            color={WsTypoColors.SECONDARY}
          >
            <WsButton variant="outlined" onClick={() => setOpenUnpinDialog(true)}>
              <Unpin />
            </WsButton>
          </WsTypography>
        </Box>
      ) : (
        <></>
      )}

      {openUnpinDialog && (
        <WsDialog openDialog={openUnpinDialog}>
          <DialogContent sx={{ minHeight: '150px', bgcolor: 'white' }}>
            <DialogTitle sx={{ textAlign: 'center', fontSize: '1.2rem', color: 'black' }}>
              Remove Dashboard from Home Page
            </DialogTitle>
            <Stack spacing={5} alignItems="center">
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY900}>
                {pinUnpinPromptText(true, selectedDashboard?.name ?? '')}
              </WsTypography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <WsButton
                  onClick={() => {
                    setOpenUnpinDialog(false);
                  }}
                  variant="text"
                  size="large"
                >
                  Cancel
                </WsButton>
                <WsButton
                  onClick={() => {
                    onConfirm(selectId ?? '');
                  }}
                  variant="contained"
                  size="large"
                >
                  Yes, Remove
                </WsButton>
              </Stack>
            </Stack>
          </DialogContent>
        </WsDialog>
      )}
    </ErrorBoundary>
  );
};

export default UnpinDashboard;
